import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import useWorkspace from '../hooks/useWorkspace';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import PaintingCanvas from '../components/ui/PaintingCanvas';
import { useToast } from '../components/ui/Toast';
import { SCENE_CATALOG, getScene, isVisibleGalleryArtwork } from '../config/membership';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiCheck, FiRotateCcw, FiArrowRight, FiImage, FiPlus } = FiIcons;

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const tomorrowDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return localDate(date);
};

const formatTaskSchedule = (task) => {
  if (!task.scheduledDate) return 'Unscheduled';

  let dateLabel;
  if (task.scheduledDate === localDate()) {
    dateLabel = 'Today';
  } else if (task.scheduledDate === tomorrowDate()) {
    dateLabel = 'Tomorrow';
  } else {
    const date = new Date(`${task.scheduledDate}T12:00:00`);
    dateLabel = Number.isNaN(date.getTime())
      ? task.scheduledDate
      : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  return task.scheduledTime
    ? `${dateLabel} · ${task.scheduledTime}`
    : dateLabel;
};

const paintingMinutes = (art) => {
  if (Number.isFinite(Number(art.length))) return Number(art.length);
  if (Number.isFinite(Number(art.durationMinutes))) return Number(art.durationMinutes);
  if (Number.isFinite(Number(art.durationMs))) return Number(art.durationMs) / 60000;
  return 0;
};

export default function Overview() {
  const { state } = useGame();
  const { tasks, addTask, updateTask, storageError } = useWorkspace();
  const { success, error } = useToast();
  const navigate = useNavigate();
  const quickAddRef = useRef(null);

  const sessionOngoing = ['active', 'paused', 'completed'].includes(state.focus.status);
  const sessionBlocksStart = ['active', 'paused'].includes(state.focus.status);
  const durationMs = state.focus.durationMs || state.focus.length * 60 * 1000;
  const focusProgress = state.focus.status === 'completed'
    ? 1
    : Math.max(0, Math.min(1, (state.focus.elapsed || 0) / durationMs));
  const defaultScene = SCENE_CATALOG[0]?.id || 'threshold-of-autumn';
  const overviewScene = sessionOngoing
    ? state.focus.scene || defaultScene
    : defaultScene;
  const overviewSeed = sessionOngoing
    ? state.focus.artSeed || 123
    : 123;

  const nextTasks = useMemo(() => tasks
    .filter((item) => item.status !== 'completed')
    .sort((a, b) => {
      const first = `${a.scheduledDate || '9999'} ${a.scheduledTime || '99:99'}`;
      const second = `${b.scheduledDate || '9999'} ${b.scheduledTime || '99:99'}`;
      return first.localeCompare(second);
    }), [tasks]);

  const paintings = useMemo(() => {
    const seen = new Set();
    return [...(state.gallery || [])]
      .filter((art) => art?.id && !seen.has(art.id) && seen.add(art.id))
      .sort((a, b) => Number(b.completedAt) - Number(a.completedAt));
  }, [state.gallery]);

  const visiblePaintings = useMemo(
    () => paintings.filter(isVisibleGalleryArtwork),
    [paintings]
  );

  const weekStart = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return date.getTime();
  }, []);

  const focusedMinutes = Math.round(paintings
    .filter((art) => Number(art.completedAt) >= weekStart)
    .reduce((total, art) => total + paintingMinutes(art), 0));

  const addQuickTask = (event) => {
    event.preventDefault();
    const title = quickAddRef.current?.value.trim();
    if (!title) {
      error('Add a task title first.');
      return;
    }
    addTask({
      title,
      status: 'todo',
      project: 'General',
      scheduledDate: localDate(),
      scheduledTime: '',
      estimatedMinutes: 25,
    });
    quickAddRef.current.value = '';
    success('Added to today.');
  };

  const focusPath = nextTasks[0]
    ? `/studio?task=${encodeURIComponent(nextTasks[0].id)}`
    : '/studio';
  const activeFocusPath = sessionOngoing ? '/studio' : focusPath;

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage mb-2">
          Your effort, made visible.
        </p>
        <h1 className="text-4xl lg:text-5xl font-cormorant font-bold text-pine mb-2">
          One thing at a time.
        </h1>
        <p className="text-pine/70 font-dm-sans text-lg">
          Welcome back, {state.profile.name}. Choose what deserves your attention.
        </p>
      </header>

      {storageError && (
        <p role="alert" className="rounded-xl bg-crimson/10 px-4 py-3 text-sm text-crimson">
          {storageError}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 flex flex-col h-full">
          <Card variant="pine" className="flex-1 p-8 overflow-hidden relative group shadow-lg min-h-[320px]">
            <div className="absolute bottom-0 right-0 w-64 lg:w-80 opacity-60 pointer-events-none">
              <PaintingCanvas
                id="overview-focus-bg"
                scene={overviewScene}
                progress={state.focus.status === 'active' ? focusProgress : sessionOngoing ? focusProgress : 1}
                artSeed={overviewSeed}
                className={`${getScene(overviewScene)?.type === 'image' ? 'aspect-square object-contain' : 'aspect-[4/3]'} rounded-tl-[3rem]`}
              />
            </div>

            <div className="relative z-10 flex flex-col h-full justify-between max-w-md">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-sm font-dm-sans text-paper/90 mb-6 border border-white/10">
                  <SafeIcon icon={FiClock} className="w-4 h-4" />
                  Up next
                  {sessionOngoing && <span className="sr-only">Current focus session</span>}
                </div>
                <h2 className="text-3xl lg:text-4xl font-cormorant font-bold mb-4 leading-tight text-paper">
                  {nextTasks[0]?.title || 'A quiet space for meaningful work.'}
                  {sessionOngoing && state.focus.taskTitle && (
                    <span className="block text-xl mt-2 text-paper/80">{state.focus.taskTitle}</span>
                  )}
                </h2>
                <p className="text-paper/70 font-dm-sans mb-8 max-w-sm">
                  {nextTasks[0]
                    ? `${nextTasks[0].estimatedMinutes || 25} minutes${nextTasks[0].project ? ` · ${nextTasks[0].project}` : ''}`
                    : 'Add something that matters, then make your focus visible.'}
                  {sessionOngoing && (
                    <span className="block mt-2">{Math.round(focusProgress * 100)}% painted</span>
                  )}
                </p>
              </div>

              <div>
                <Button
                  variant="paper"
                  onClick={() => navigate(activeFocusPath)}
                  icon={FiArrowRight}
                  iconPosition="right"
                >
                  {state.focus.status === 'completed'
                    ? 'Return to Studio'
                    : nextTasks[0] ? 'Start focus' : 'Try the studio'}
                  {sessionBlocksStart && <span className="sr-only"> Return to session</span>}
                </Button>
                {sessionBlocksStart && (
                  <button
                    type="button"
                    onClick={() => navigate('/studio')}
                    className="block mt-3 text-sm font-bold text-paper underline underline-offset-4"
                  >
                    Return to session
                  </button>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 flex flex-col gap-4">
          <Card className="p-5 bg-apricot/10">
            <h2 className="text-xl font-cormorant font-bold text-pine">Add to today</h2>
            <form onSubmit={addQuickTask} className="flex gap-2 mt-4">
              <label htmlFor="overview-quick-task" className="sr-only">Task title</label>
              <input
                id="overview-quick-task"
                ref={quickAddRef}
                className="min-w-0 flex-1 rounded-2xl border border-pine/15 bg-white px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
                placeholder="What needs attention?"
              />
              <Button type="submit" size="sm" icon={FiPlus} aria-label="Add task">Add</Button>
            </form>
          </Card>

          <Card className="p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-pine/50">Your week</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-2xl bg-sage/15 p-4">
                <SafeIcon icon={FiClock} className="text-sage mb-2" />
                <strong className="block text-2xl text-pine">{focusedMinutes}</strong>
                <span className="text-xs text-pine/60">focused minutes</span>
              </div>
              <div className="rounded-2xl bg-lavender/15 p-4">
                <SafeIcon icon={FiImage} className="text-lavender mb-2" />
                <strong className="block text-2xl text-pine">{visiblePaintings.length}</strong>
                <span className="text-xs text-pine/60">paintings</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/analytics')}
              className="mt-4 text-sm font-bold text-pine/60 hover:text-pine underline underline-offset-4"
            >
              Your progress
            </button>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-2xl font-cormorant font-bold text-pine">Today’s next steps</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/plan')}>
              Plan your day
            </Button>
          </div>

          {nextTasks.length > 0 ? (
            <div className="divide-y divide-pine/10">
              {nextTasks.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-center gap-3 py-3">
                  <button
                    type="button"
                    onClick={() => updateTask(item.id, { status: 'completed' })}
                    className="w-9 h-9 rounded-xl border border-pine/15 text-pine hover:bg-sage/20 flex items-center justify-center"
                    aria-label={`Complete ${item.title}`}
                  >
                    <SafeIcon icon={FiCheck} />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/studio?task=${encodeURIComponent(item.id)}`)}
                    className="min-w-0 flex-1 text-left"
                    disabled={sessionBlocksStart}
                    aria-disabled={sessionBlocksStart}
                  >
                    <span className="block font-bold text-pine truncate">{item.title}</span>
                    <span className="text-xs text-pine/50">
                      {formatTaskSchedule(item)} · {item.estimatedMinutes || 25} min
                    </span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-pine/20 p-8 text-center">
              <p className="text-pine/60">Nothing is waiting. Add a task or begin a free focus session.</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                <Button size="sm" onClick={() => navigate('/studio')}>Try the studio</Button>
                <Button size="sm" variant="secondary" onClick={() => navigate('/plan')}>Plan your day</Button>
              </div>
            </div>
          )}

          {tasks.some((item) => item.status === 'completed') && (
            <button
              type="button"
              onClick={() => {
                const latest = [...tasks].reverse().find((item) => item.status === 'completed');
                if (latest) updateTask(latest.id, { status: 'todo' });
              }}
              className="mt-4 text-xs font-bold text-pine/55 hover:text-pine inline-flex items-center gap-1"
            >
              <SafeIcon icon={FiRotateCcw} />
              Undo latest completion
            </button>
          )}
        </Card>

        <Card className="lg:col-span-5 p-6 bg-lavender/10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-cormorant font-bold text-pine">Recent work</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/studio')}>Studio</Button>
          </div>

          {visiblePaintings.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {visiblePaintings.slice(0, 2).map((art) => (
                <button
                  key={art.id}
                  type="button"
                  onClick={() => navigate('/studio')}
                  className="text-left group"
                >
                  <div className={`${getScene(art.scene)?.type === 'image' ? 'aspect-square' : 'aspect-[4/3]'} rounded-2xl overflow-hidden border border-pine/10`}>
                    <PaintingCanvas
                      id={`overview-canvas-${art.id}`}
                      scene={art.scene}
                      progress={1}
                      artSeed={art.artSeed}
                      className="w-full h-full group-hover:scale-[1.02] transition-transform"
                    />
                  </div>
                  <span className="block mt-2 text-xs font-bold capitalize text-pine">
                    {getScene(art.scene)?.name || (art.scene || 'painting').replace('-', ' ')}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-pine/60">
              Completed focus paintings will appear here.
            </p>
          )}
        </Card>
      </div>

      <div className="text-center flex flex-wrap items-center justify-center gap-5">
        <button
          type="button"
          onClick={() => navigate('/membership')}
          className="text-sm font-bold text-pine/60 hover:text-pine underline underline-offset-4"
        >
          Explore Studio Plus
        </button>
        <button
          type="button"
          onClick={() => navigate('/welcome')}
          className="text-sm font-bold text-pine/60 hover:text-pine underline underline-offset-4"
        >
          About MindGarden
        </button>
      </div>
    </div>
  );
}